<template>
  <v-container fluid class="ncrm-onboarding-board">

    <!-- Page header -->
    <v-card class="pa-6 ncrm-onboarding-board__header-card">
      <div class="d-flex flex-wrap align-center justify-space-between ga-4">
        <div class="text-left">
          <div class="d-flex align-center ga-1 mb-1">
            <v-btn
              variant="text"
              color="primary"
              size="small"
              class="ncrm-onboarding-board__back-btn px-1"
              @click="goBack"
            >
              <v-icon start size="15">mdi-arrow-left</v-icon>
              {{ $t("ncrm.onboarding.home.title") }}
            </v-btn>
          </div>
          <h2 class="text-h5 ncrm-text-strong my-0">{{ boardTitle }}</h2>
          <div v-if="boardSubtitle" class="text-body-2 text-medium-emphasis mt-1">{{ boardSubtitle }}</div>
        </div>

        <div class="d-flex align-center ga-2 flex-wrap">
          <v-btn variant="text" color="primary" @click="openTemplates">
            <v-icon start>mdi-shape-outline</v-icon>
            {{ $t("ncrm.onboarding.board.actions.templates") }}
          </v-btn>
          <v-btn variant="text" color="primary" @click="openContracts">
            <v-icon start>mdi-file-document-outline</v-icon>
            {{ $t("ncrm.onboarding.board.actions.contracts") }}
          </v-btn>
          <v-btn variant="text" color="primary" :loading="loading" @click="loadBoardData">
            <v-icon start>mdi-refresh</v-icon>
            {{ $t("ncrm.onboarding.actions.refresh") }}
          </v-btn>
        </div>
      </div>

      <!-- Filters bar -->
      <div class="d-flex flex-wrap align-center ga-3 mt-4">
        <v-text-field
          v-model="filters.search"
          :label="$t('ncrm.onboarding.board.filters.search')"
          density="compact"
          clearable
          hide-details
          style="max-width: 280px; min-width: 160px"
          @keyup.enter="applyFilters"
          @click:clear="applyFilters"
        />
        <v-select
          v-model="filters.status"
          :label="$t('ncrm.onboarding.board.filters.status')"
          :items="statusItems"
          item-title="label"
          item-value="value"
          density="compact"
          hide-details
          style="max-width: 200px; min-width: 130px"
          @update:model-value="onStatusChange"
        />
        <v-btn color="primary" variant="tonal" density="comfortable" @click="applyFilters">
          <v-icon start>mdi-filter-variant</v-icon>
          {{ $t("ncrm.onboarding.board.filters.apply") }}
        </v-btn>
      </div>
    </v-card>

    <!-- View tabs -->
    <div class="mt-3 d-flex align-center flex-wrap ga-2">
      <NcrmPreferredViewTabs
        v-model="activeView"
        :items="viewItems"
        :preferred-value="preferredView"
        :aria-label="$t('ncrm.shared.viewPreferences.tabsAriaLabel')"
        @set-default="setPreferredView"
      />
    </div>

    <!-- ── Board view: grouped by product ───────────────────── -->
    <template v-if="activeView === 'board'">
      <v-alert
        v-if="!loading && !productBuckets.length"
        type="info"
        class="mt-2"
      >
        {{ $t("ncrm.onboarding.board.empty") }}
      </v-alert>

      <div v-else class="mt-2">
        <div
          v-for="group in productBuckets"
          :key="group.key"
          class="ncrm-onboarding-board__section"
        >
          <!-- Section header -->
          <div class="ncrm-onboarding-board__section-header">
            <v-icon size="15" class="ncrm-onboarding-board__section-icon">mdi-package-variant-outline</v-icon>
            <span class="ncrm-onboarding-board__section-title">{{ group.label }}</span>
            <v-chip size="x-small" class="ncrm-onboarding-board__chip-neutral">
              {{ group.items.length }}
            </v-chip>
          </div>

          <!-- Cards grid -->
          <div class="ncrm-onboarding-board__cards-grid">
            <article
              v-for="entry in group.items"
              :key="entry.id"
              class="ncrm-onboarding-board__entry-card"
              :class="{ 'ncrm-onboarding-board__entry-card--overdue': hasOverdueTasks(entry) }"
              role="button"
              tabindex="0"
              @click="openOnboardingDetails(entry.id)"
              @keydown.enter.prevent="openOnboardingDetails(entry.id)"
              @keydown.space.prevent="openOnboardingDetails(entry.id)"
            >
              <!-- Zone 1: Header — contract label + status chip -->
              <div class="d-flex align-start justify-space-between ga-2">
                <div class="ncrm-onboarding-board__entry-main">
                  <div class="text-body-2 font-weight-bold ncrm-text-strong ncrm-onboarding-board__entry-title">
                    {{ contractLabel(entry) }}
                  </div>
                  <!-- Zone 2: Secondary identifier — company name -->
                  <div class="ncrm-onboarding-board__entry-id">
                    {{ companyLabel(entry) }}
                  </div>
                </div>
                <v-chip
                  size="x-small"
                  :color="statusColor(entry.status)"
                  class="flex-shrink-0 ncrm-onboarding-board__entry-status"
                >
                  {{ statusLabel(entry.status) }}
                </v-chip>
              </div>

              <!-- Zone 3: Meta properties -->
              <div class="ncrm-onboarding-board__entry-meta mt-2">
                <div class="ncrm-onboarding-board__entry-meta-row">
                  <v-icon size="14" class="ncrm-onboarding-board__entry-icon">mdi-layers-outline</v-icon>
                  <span>{{ stageLabel(entry) }}</span>
                </div>
                <div class="ncrm-onboarding-board__entry-meta-row">
                  <v-icon size="14" class="ncrm-onboarding-board__entry-icon">mdi-account-outline</v-icon>
                  <span>{{ ownerLabel(entry) }}</span>
                </div>
              </div>

              <!-- Zone 4: KPI chips -->
              <div class="d-flex flex-wrap ga-2 mt-2">
                <v-chip color="info">
                  <v-icon start size="12">mdi-check-circle-outline</v-icon>
                  {{ $t("ncrm.onboarding.board.cards.tasksProgress", {
                    done: formatNumber(entry.done_task_count),
                    total: formatNumber(entry.total_task_count),
                    percent: completionPercent(entry),
                  }) }}
                </v-chip>
                <v-chip
                  size="small"
                  :variant="hasOverdueTasks(entry) ? 'flat' : 'tonal'"
                  :color="hasOverdueTasks(entry) ? 'error' : 'success'"
                >
                  <v-icon start size="12">
                    {{ hasOverdueTasks(entry) ? "mdi-clock-alert-outline" : "mdi-clock-check-outline" }}
                  </v-icon>
                  {{ formatNumber(entry.overdue_task_count) }}
                </v-chip>
              </div>

              <!-- Zone 5: Footer — timestamp -->
              <div class="ncrm-onboarding-board__entry-footer mt-auto">
                <v-icon size="13" class="ncrm-onboarding-board__entry-footer-icon">mdi-clock-outline</v-icon>
                <span>{{ formatDateTime(entry.updated_at) }}</span>
              </div>
            </article>
          </div>
        </div>
      </div>
    </template>

    <!-- ── Table view ────────────────────────────────────────── -->
    <v-card class="mt-2" v-else>
      <v-data-table-server
        :headers="headers"
        :items="entries"
        :loading="loading"
        :items-per-page="pagination.limit"
        :items-length="pagination.total"
        :page="pagination.page"
        item-key="id"
        class="elevation-0 ncrm-onboarding-board__table"
        density="comfortable"
        :row-props="tableRowProps"
        @click:row="onTableRowClick"
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
      >
        <template #item.contract_number="{ item }">
          <div class="d-flex align-center ga-2">
            <v-icon size="14" class="ncrm-onboarding-board__table-cell-icon">mdi-file-document-outline</v-icon>
            <span
              class="ncrm-text-strong ncrm-onboarding-board__table-cell-inline"
              :title="contractLabel(item)"
            >
              {{ contractLabel(item) }}
            </span>
          </div>
        </template>

        <template #item.company_name="{ item }">
          {{ companyLabel(item) }}
        </template>

        <template #item.product_name_snapshot="{ item }">
          {{ productLabel(item) }}
        </template>

        <template #item.status="{ item }">
          <v-chip size="x-small" :color="statusColor(item.status)">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template #item.health="{ item }">
          <v-chip size="x-small" :color="healthColor(item.health)">
            {{ healthLabel(item.health) }}
          </v-chip>
        </template>

        <template #item.open_task_count="{ item }">
          {{ formatNumber(item.open_task_count) }}
        </template>

        <template #item.overdue_task_count="{ item }">
          <span :class="{ 'ncrm-onboarding-board__metric-overdue': hasOverdueTasks(item) }">
            {{ formatNumber(item.overdue_task_count) }}
          </span>
        </template>

        <template #item.updated_at="{ item }">
          <div class="d-flex align-center ga-1 text-no-wrap">
            <v-icon size="13" style="opacity: 0.38">mdi-clock-outline</v-icon>
            {{ formatDateTime(item.updated_at) }}
          </div>
        </template>

        <template #item.row_nav>
          <v-icon size="16" class="ncrm-onboarding-board__table-chevron">mdi-chevron-right</v-icon>
        </template>

        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t("ncrm.onboarding.board.empty") }}
          </v-alert>
        </template>
      </v-data-table-server>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmPreferredViewTabs from "@/components/ncrm/shared/NcrmPreferredViewTabs.vue";
import {
  loadPreferredView,
  resolveViewPreferencePageKey,
  savePreferredView,
} from "@/Utile/ncrmViewPreferences.mjs";
import { formatDisplayNumber } from "@/Utile/displayFormat.mjs";

const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const ONBOARDING_BOARD_VIEW_ITEMS = Object.freeze([{ value: "board" }, { value: "table" }]);
const ONBOARDING_BOARD_PAGE_KEY_FALLBACK = "/ncrm/onboarding/boards";

const resolveDefaultStatusFilter = (context) => {
  const parsed = Number(context?.productId || context?.$route?.params?.productId || 0);
  return Number.isFinite(parsed) && parsed > 0 ? "active" : "";
};

export default {
  name: "NcrmOnboardingBoard",
  components: {
    NcrmPreferredViewTabs,
  },
  props: {
    productId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    const preferredView =
      loadPreferredView({
        pageKey: resolveViewPreferencePageKey(this.$route, ONBOARDING_BOARD_PAGE_KEY_FALLBACK),
        items: ONBOARDING_BOARD_VIEW_ITEMS,
        fallback: "board",
      }) || "board";
    return {
      loading: false,
      activeView: preferredView,
      preferredView,
      boardMeta: null,
      entries: [],
      pagination: {
        page: 1,
        limit: 50,
        total: 0,
        pages: 1,
      },
      filters: {
        search: "",
        status: resolveDefaultStatusFilter(this),
      },
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    viewItems() {
      return [
        { value: "board", label: this.$t("ncrm.onboarding.board.views.board") },
        { value: "table", label: this.$t("ncrm.onboarding.board.views.table") },
      ];
    },
    normalizedProductId() {
      const parsed = Number(this.productId || this.$route?.params?.productId || 0);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return Math.trunc(parsed);
    },
    activeKpiFilter() {
      return String(this.$route?.query?.kpi || "").trim().toLowerCase();
    },
    isAggregateBoard() {
      return !this.normalizedProductId;
    },
    boardTitle() {
      if (this.isAggregateBoard && this.activeKpiFilter) {
        const keyMap = {
          active: "ncrm.onboarding.home.kpi.active",
          blocked: "ncrm.onboarding.home.kpi.blocked",
          overdue_tasks: "ncrm.onboarding.home.kpi.overdueTasks",
          completed: "ncrm.onboarding.home.kpi.completed",
        };
        const titleKey = keyMap[this.activeKpiFilter];
        if (titleKey) return this.$t(titleKey);
      }
      const productName = String(this.boardMeta?.product_name || "").trim();
      if (productName) {
        return this.$t("ncrm.onboarding.board.titleWithProduct", { product: productName });
      }
      if (this.normalizedProductId) {
        return this.$t("ncrm.onboarding.board.titleWithProduct", { product: `#${this.normalizedProductId}` });
      }
      return this.$t("ncrm.onboarding.board.titleFallback");
    },
    boardSubtitle() {
      return this.isAggregateBoard
        ? this.$t("ncrm.onboarding.board.aggregateSubtitle")
        : this.$t("ncrm.onboarding.board.subtitle");
    },
    statusItems() {
      return [
        { label: this.$t("ncrm.onboarding.board.filters.allStatuses"), value: "" },
        { label: this.statusLabel("active"), value: "active" },
        { label: this.statusLabel("blocked"), value: "blocked" },
        { label: this.statusLabel("completed"), value: "completed" },
        { label: this.statusLabel("canceled"), value: "canceled" },
      ];
    },
    headers() {
      const base = [
        { title: this.$t("ncrm.onboarding.board.columns.contract"), key: "contract_number" },
        { title: this.$t("ncrm.onboarding.board.columns.company"), key: "company_name" },
        { title: this.$t("ncrm.onboarding.board.columns.stage"), key: "current_stage_name" },
        { title: this.$t("ncrm.onboarding.board.columns.status"), key: "status", sortable: false },
        { title: this.$t("ncrm.onboarding.board.columns.health"), key: "health", sortable: false },
        { title: this.$t("ncrm.onboarding.board.columns.openTasks"), key: "open_task_count" },
        { title: this.$t("ncrm.onboarding.board.columns.overdueTasks"), key: "overdue_task_count", sortable: false },
        { title: this.$t("ncrm.onboarding.board.columns.updatedAt"), key: "updated_at" },
        { title: "", key: "row_nav", sortable: false, width: "48px", align: "center" },
      ];
      if (this.isAggregateBoard) {
        base.splice(1, 0, {
          title: this.$t("ncrm.onboarding.home.board.productLabel"),
          key: "product_name_snapshot",
        });
      }
      return base;
    },
    productBuckets() {
      const productMap = new Map();
      for (const entry of this.entries) {
        const productId = parseNumeric(entry?.product_id, 0);
        const productKey = productId > 0 ? String(productId) : String(entry?.product_name_snapshot || "unknown").trim() || "unknown";
        const productName =
          String(entry?.product_name_snapshot || "").trim() ||
          (productId > 0 ? `#${productId}` : this.$t("ncrm.onboarding.board.stageFallback"));

        if (!productMap.has(productKey)) {
          productMap.set(productKey, {
            key: productKey,
            label: productName,
            sortName: productName.toLowerCase(),
            items: [],
          });
        }
        productMap.get(productKey).items.push(entry);
      }

      const sortEntries = (items) =>
        items.slice().sort((a, b) => {
          const aDate = String(a?.updated_at || "").trim();
          const bDate = String(b?.updated_at || "").trim();
          if (aDate === bDate) return parseNumeric(b?.id, 0) - parseNumeric(a?.id, 0);
          return aDate < bDate ? 1 : -1;
        });

      return Array.from(productMap.values())
        .map((bucket) => ({ ...bucket, items: sortEntries(bucket.items) }))
        .sort((a, b) => a.sortName.localeCompare(b.sortName, this.$i18n?.locale || "ro-RO"));
    },
    /**
     * Groups entries by stage key, sorted by stage sort_order (template order).
     * Used for stage-lane board views.
     */
    stageBuckets() {
      const stageMap = new Map();
      for (const entry of this.entries) {
        const stageKey = String(entry?.current_stage_key || "unknown").trim() || "unknown";
        const stageName = String(entry?.current_stage_name || stageKey).trim();
        const stageSortOrder = parseNumeric(entry?.current_stage_sort_order, 99999);

        if (!stageMap.has(stageKey)) {
          stageMap.set(stageKey, {
            key: stageKey,
            label: stageName,
            sortOrder: stageSortOrder,
            items: [],
          });
        }
        stageMap.get(stageKey).items.push(entry);
      }

      const sortEntries = (items) =>
        items.slice().sort((a, b) => {
          const aDate = String(a?.updated_at || "").trim();
          const bDate = String(b?.updated_at || "").trim();
          if (aDate === bDate) return parseNumeric(b?.id, 0) - parseNumeric(a?.id, 0);
          return aDate < bDate ? 1 : -1;
        });

      return Array.from(stageMap.values())
        .map((bucket) => ({ ...bucket, items: sortEntries(bucket.items) }))
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
    /** Flat list of board cards for the active product view. */
    boardCards() {
      return this.entries;
    },
  },
  watch: {
    "$route.params.productId"(nextValue, previousValue) {
      if (String(nextValue || "") === String(previousValue || "")) return;
      this.restorePreferredView();
      this.pagination.page = 1;
      this.filters.status = resolveDefaultStatusFilter(this);
      this.loadBoardData();
    },
    "$route.query.kpi"(nextValue, previousValue) {
      if (String(nextValue || "") === String(previousValue || "")) return;
      this.pagination.page = 1;
      this.loadBoardData();
    },
  },
  mounted() {
    this.loadBoardData();
  },
  methods: {
    viewPreferencePageKey() {
      return resolveViewPreferencePageKey(this.$route, ONBOARDING_BOARD_PAGE_KEY_FALLBACK);
    },
    showSnackbar(message, color = "info") {
      this.snackbar = { show: true, color, message };
    },
    restorePreferredView() {
      this.preferredView =
        loadPreferredView({
          pageKey: this.viewPreferencePageKey(),
          items: ONBOARDING_BOARD_VIEW_ITEMS,
          fallback: "board",
        }) || "board";
      this.activeView = this.preferredView;
    },
    setPreferredView(value) {
      const preferredView = savePreferredView({
        pageKey: this.viewPreferencePageKey(),
        value,
        items: ONBOARDING_BOARD_VIEW_ITEMS,
      });
      if (!preferredView) return;
      this.preferredView = preferredView;
      if (preferredView !== this.activeView) {
        this.activeView = preferredView;
      }
    },
    formatNumber(value) {
      return formatDisplayNumber(parseNumeric(value, 0));
    },
    formatDateTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.${parsed.getFullYear()}, ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    },
    hasOverdueTasks(entry) {
      return parseNumeric(entry?.overdue_task_count, 0) > 0;
    },
    stageLabel(entry) {
      return String(entry?.current_stage_name || "").trim() || this.$t("ncrm.onboarding.board.stageFallback");
    },
    tableRowProps() {
      return { style: "cursor: pointer" };
    },
    statusLabel(status) {
      const normalized = String(status || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.onboarding.statuses.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    healthLabel(health) {
      const normalized = String(health || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.onboarding.health.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    statusColor(status) {
      const normalized = String(status || "").trim().toLowerCase();
      if (normalized === "blocked") return "warning";
      if (normalized === "completed") return "success";
      if (normalized === "canceled") return "error";
      return "primary";
    },
    healthColor(health) {
      const normalized = String(health || "").trim().toLowerCase();
      if (normalized === "red") return "error";
      if (normalized === "yellow") return "warning";
      return "success";
    },
    contractLabel(entry) {
      const contractNumber = String(entry?.contract_number || "").trim();
      if (contractNumber) return contractNumber;
      const contractName = String(entry?.contract_name || "").trim();
      if (contractName) return contractName;
      const contractId = parseNumeric(entry?.contract_id, 0);
      return contractId > 0 ? `#${contractId}` : "-";
    },
    companyLabel(entry) {
      const companyName = String(entry?.company_name || "").trim();
      if (companyName) return companyName;
      const dealName = String(entry?.deal_name || "").trim();
      if (dealName) return dealName;
      return "-";
    },
    productLabel(entry) {
      const productName = String(entry?.product_name_snapshot || "").trim();
      if (productName) return productName;
      const productId = parseNumeric(entry?.product_id, 0);
      return productId > 0 ? `#${productId}` : "-";
    },
    ownerLabel(entry) {
      return String(entry?.owner_name || "").trim() || "-";
    },
    /** Returns task completion percentage (0–100) for a board entry. */
    completionPercent(entry) {
      const total = parseNumeric(entry?.total_task_count, 0);
      if (total <= 0) return 0;
      const done = parseNumeric(entry?.done_task_count, 0);
      return Math.round((done / total) * 100);
    },
    goBack() {
      this.$router.push({ name: "NcrmOnboardingHome" });
    },
    openContracts() {
      this.$router.push({ name: "NcrmContractsHome" });
    },
    openTemplates() {
      this.$router.push({ name: "NcrmOnboardingTemplates" });
    },
    openOnboardingDetails(onboardingId) {
      const normalizedOnboardingId = parseNumeric(onboardingId, 0);
      if (!normalizedOnboardingId) return;
      this.$router.push({
        name: "NcrmOnboardingDetails",
        params: { onboardingId: String(normalizedOnboardingId) },
      });
    },
    applyFilters() {
      this.pagination.page = 1;
      this.loadEntries();
    },
    onStatusChange(nextValue) {
      this.filters.status = typeof nextValue === "string" ? nextValue : "";
      this.applyFilters();
    },
    onPageChange(page) {
      const normalized = Number(page || 1);
      this.pagination.page = Number.isFinite(normalized) && normalized > 0 ? Math.trunc(normalized) : 1;
      this.loadEntries();
    },
    onItemsPerPageChange(limit) {
      const normalized = Number(limit || 50);
      const nextLimit =
        Number.isFinite(normalized) && normalized > 0 ? Math.min(Math.trunc(normalized), 200) : 50;
      if (nextLimit === this.pagination.limit) return;
      this.pagination.limit = nextLimit;
      this.pagination.page = 1;
      this.loadEntries();
    },
    normalizeTableRowPayload(payload) {
      if (!payload) return null;
      if (payload.raw) return payload.raw;
      if (payload.item?.raw) return payload.item.raw;
      if (payload.item) return payload.item;
      return payload;
    },
    onTableRowClick(_, payload) {
      const row = this.normalizeTableRowPayload(payload);
      const onboardingId = parseNumeric(row?.id, 0);
      if (!onboardingId) return;
      this.openOnboardingDetails(onboardingId);
    },
    async loadBoardMeta() {
      if (!this.normalizedProductId) {
        this.boardMeta = null;
        return;
      }
      const { data } = await axios.get("/ncrm/onboarding/boards");
      const items = Array.isArray(data?.items) ? data.items : [];
      this.boardMeta =
        items.find((item) => parseNumeric(item?.product_id, 0) === this.normalizedProductId) || null;
    },
    async loadEntries() {
      this.loading = true;
      try {
        const endpoint = this.normalizedProductId
          ? `/ncrm/onboarding/boards/${this.normalizedProductId}/entries`
          : "/ncrm/onboarding/boards/entries";
        const { data } = await axios.get(endpoint, {
          params: {
            page: this.pagination.page,
            limit: this.pagination.limit,
            search: this.filters.search.trim() || undefined,
            status: this.filters.status || undefined,
            kpi: this.isAggregateBoard ? this.activeKpiFilter || undefined : undefined,
          },
        });
        this.entries = Array.isArray(data?.items) ? data.items : [];
        this.pagination.total = parseNumeric(data?.total, 0);
        this.pagination.pages = parseNumeric(data?.pages, 1);
      } catch (error) {
        console.error("[ncrm-onboarding-board] load entries failed", error);
        this.entries = [];
        this.pagination.total = 0;
        this.pagination.pages = 1;
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.board.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async loadBoardData() {
      this.loading = true;
      try {
        await this.loadBoardMeta();
      } catch (error) {
        console.error("[ncrm-onboarding-board] load board meta failed", error);
        this.boardMeta = null;
      } finally {
        this.loading = false;
      }
      await this.loadEntries();
    },
  },
};
</script>

<style src="./NcrmOnboardingBoard.styles.css" />
